import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {withRouter} from 'react-router-dom';
import {Alert, Button, Form, Modal, Row, Spinner} from 'react-bootstrap';
import {Formik} from 'formik';
import Configuration from '../config';
import Iframe from 'react-iframe';
import {saveHighlight, permanentVideoSelected, hideVideoHighlightModal} from '../actions/player';
import * as partnerActions from '../actions/partner';
import {showMessage} from '../actions/messages';
import {push} from 'connected-react-router';
import * as api from '../api';
import 'rc-slider/assets/index.css';
import {useTranslation} from 'react-i18next';
import {askInvoiceRequest} from '../utils/invoice-request';
import {FaChartBar, FaClock, FaMagic, FaPlay, FaSave, FaTimes, FaVideo} from 'react-icons/fa';
import HighlightCameraSelector from './highlight-camera-selector';

const PlayerHighLight = ({video, token, profile, saveHighlight, showMessageCreatingHighlight, showMessageCreatingAnalysis, showMessageAnalysisCreated, showMessageHighlightDurationError, hideVideoHighlightModal, isVideoHighlightModalVisible, goToTopUp}) => {
  const {t} = useTranslation();
  const [analysisConfig, setAnalysisConfig] = useState(null);
  const [analysisConfigLoading, setAnalysisConfigLoading] = useState(false);
  const [analysisError, setAnalysisError] = useState(null);
  const [analysisSubmitting, setAnalysisSubmitting] = useState(false);
  const [pendingAnalysisData, setPendingAnalysisData] = useState(null);
  const isAnalysisMode = video.analysisMode === true;
  const maxSeconds = isAnalysisMode ? 60 * 30 : 60 * 3;

  useEffect(() => {
    if (!isVideoHighlightModalVisible || !isAnalysisMode || !video.contentType || !video.contentId) {
      return;
    }

    let mounted = true;
    setAnalysisConfig(null);
    setAnalysisError(null);
    setAnalysisConfigLoading(true);
    api.getVideoAnalysisConfig(video.contentType, video.contentId, token).then(result => {
      if (!mounted) return;
      setAnalysisConfigLoading(false);
      if (result.status >= 200 && result.status < 300 && result.json && result.json.enabled !== false) {
        setAnalysisConfig(result.json);
        return;
      }
      const errorCode = result.json && result.json.error;
      const resolvedSport = result.json && result.json.sport ? ` Sport risolto dal backend: ${result.json.sport}.` : '';
      setAnalysisError(errorCode === 'analysis_not_configured'
        ? `Analisi non configurata per questo sport. Verifica in admin la configurazione AI per pickleball.${resolvedSport}`
        : 'Non e` stato possibile caricare la configurazione analisi.');
    });

    return () => {
      mounted = false;
    };
  }, [isVideoHighlightModalVisible, isAnalysisMode, video.contentType, video.contentId, token]);

  const walletCents = Math.round(Number(profile && profile.user ? profile.user.wallet : 0) * 100);
  const formatEurCents = cents => `${((Number(cents) || 0) / 100).toFixed(2)} EUR`;

  const hmsToSeconds = val => {
    const p = val.split(':');
    let s = 0;
    let m = 1;

    while (p.length > 0) {
      s += m * parseInt(p.pop(), 10);
      m *= 60;
    }

    return s;
  };

  const onSubmit = data => {
    let iframeRef = document.getElementById("higlightIframe");
    if (!iframeRef) {
      return;
    }

    if (data.end - data.start > maxSeconds) {
      showMessageHighlightDurationError();
      return;
    }

    const tracking = [
      {
        seconds: data.start,
      },
      {
        seconds: data.end,
      }
    ]
    const videoData = {
      videoId: data.videoId,
      slotId: data.slotId,
      sliceHighlight: data.sliceHighlight,
      name: data.name,
      description: data.description,
      tracking,
      selectedCameras: data.selectedCameras
    };
    if (isAnalysisMode) {
      setAnalysisError(null);
      const durationSeconds = Number(data.end) - Number(data.start);
      const minutes = Math.max(1, Math.ceil(durationSeconds / 60));
      const priceCents = minutes * (analysisConfig ? Number(analysisConfig.pricePerMinuteCents) || 0 : 0);
      setPendingAnalysisData({
        contentType: data.contentType,
        contentId: data.contentId,
        title: data.name,
        description: data.description,
        startSecond: data.start,
        endSecond: data.end,
        selectedCameras: data.selectedCameras,
        minutes,
        priceCents,
        durationSeconds
      });
      return;
    }

    showMessageCreatingHighlight();
    saveHighlight(videoData);
  };

  const createPendingAnalysis = () => {
    if (!pendingAnalysisData) {
      return;
    }
    // Le richieste del partner seguono lo stesso pagamento wallet del player.
    // La fatturazione player, invece, richiede dati anagrafici non presenti nel
    // profilo partner e non deve bloccare l'invio dell'analisi.
    const invoiceChoice = profile && profile.user && profile.user.role === 1
      ? {blocked: false, invoiceRequested: false}
      : askInvoiceRequest(profile);
    if (invoiceChoice.blocked) return;
    setAnalysisSubmitting(true);
    setAnalysisError(null);
    showMessageCreatingAnalysis();
    api.createVideoAnalysisRequest(pendingAnalysisData, token, invoiceChoice.invoiceRequested).then(result => {
      setAnalysisSubmitting(false);
      if (result.status >= 200 && result.status < 300) {
        setPendingAnalysisData(null);
        showMessageAnalysisCreated();
        hideVideoHighlightModal();
        return;
      }
      const errorCode = result.json && result.json.error;
      const messages = {
        slice_range_not_available: t('HIGHLIGHT_ANALYSIS_SLICE_UNAVAILABLE'),
        not_enough_credit: t('HIGHLIGHT_ANALYSIS_INSUFFICIENT_CREDIT'),
        play_not_allowed: t('HIGHLIGHT_ANALYSIS_PLAY_NOT_ALLOWED'),
      };
      setPendingAnalysisData(null);
      setAnalysisError(messages[errorCode] || (errorCode
        ? t('HIGHLIGHT_ANALYSIS_ERROR_CODE', {status: result.status, error: errorCode})
        : t('HIGHLIGHT_ANALYSIS_CREATE_ERROR')));
    });
  };

  const closeConfirmation = () => {
    if (!analysisSubmitting) {
      setPendingAnalysisData(null);
    }
  };

  const rechargeCredit = () => {
    setPendingAnalysisData(null);
    hideVideoHighlightModal();
    goToTopUp();
  };

  const playerUrl = video.sliceHighlight ?
    `${Configuration.krpanoPlayerUrl}?slotId=${video.slotId}&token=${video.token}` :
    `${Configuration.krpanoPlayerUrl}?slotId=${video.slotId}&videoId=${video.videoId}&token=${video.token}`;

  const getCurrentVideoSeconds = iframeRef => {
    const krpano = iframeRef.contentWindow.krpano;
    const localSeconds = Number(krpano.get("plugin[video].time") || 0);

    if (!video.sliceHighlight) {
      return localSeconds;
    }

    const sliceOffsetSeconds = Number(krpano.get("player_settings.tvideo") || 0);
    return sliceOffsetSeconds + localSeconds;
  };

  const seekVideo = (iframeRef, seconds) => {
    const krpano = iframeRef.contentWindow.krpano;

    if (video.sliceHighlight) {
      krpano.call(`ricercasumultivideo(${seconds})`);
      return;
    }

    krpano.set("plugin[video].time", seconds);
  };


  return (
    <React.Fragment>
    <Modal className="modal_rp skywork-highlight-creator-modal highlight-camera-modal" size="xl" contentClassName="bg_blue_rp" show={isVideoHighlightModalVisible} onHide={hideVideoHighlightModal}>
      <Modal.Header closeButton>
        <Modal.Title as="h5">
          {isAnalysisMode ? <FaChartBar aria-hidden="true"/> : <FaMagic aria-hidden="true"/>}
          <span>{isAnalysisMode ? t('VIDEO_ANALYSIS_TITLE') : t('CREATE_HIGHLIGHT')}</span>
        </Modal.Title>
      </Modal.Header>
      <Formik
        enableReinitialize
        initialValues={{videoId: video.videoId, slotId: video.slotId, contentType: video.contentType, contentId: video.contentId, sliceHighlight: video.sliceHighlight, name: video.analysisTitle || null, description: null, start: 0, startYaw: 0, startPitch: 0, startFov: 0, end: 0, endYaw: 0, endPitch: 0, endFov: 0, startStr: '00:00:00', endStr: '00:00:00', cameras: video.cameras, selectedCameras: video.cameras && video.cameras.length === 1 ? video.cameras : []}}
        onSubmit={onSubmit}
      >
        {({
          handleSubmit,
          handleChange,
          values,
          setFieldValue
        }) => {

          const setStartFromSeekTo = () => {
            let iframeRef = document.getElementById("higlightIframe");
            if (!iframeRef) {
              return;
            }

            try {
              const startSeconds = getCurrentVideoSeconds(iframeRef);
              if (startSeconds === null) {
                return;
              }

              setFieldValue('start', startSeconds);
              setFieldValue('startStr', new Date(startSeconds * 1000).toISOString().slice(11, 19));
            } catch (error) {
              const handleMessage = event => {
                if (!event.data || event.data.type !== 'replayer:video-time' || event.data.field !== 'start') {
                  return;
                }
                window.removeEventListener('message', handleMessage);
                if (event.data.time === null) {
                  return;
                }
                setFieldValue('start', event.data.time);
                setFieldValue('startStr', new Date(event.data.time * 1000).toISOString().slice(11, 19));
              };
              window.addEventListener('message', handleMessage);
              iframeRef.contentWindow.postMessage({type: 'replayer:get-video-time', field: 'start'}, '*');
            }
          };

          const setEndFromSeekTo = () => {
            let iframeRef = document.getElementById("higlightIframe");
            if (iframeRef === null) {
              return;
            }

            try {
              const endSeconds = getCurrentVideoSeconds(iframeRef);
              if (endSeconds === null) {
                return;
              }

              setFieldValue('end', endSeconds);
              setFieldValue('endStr', new Date(endSeconds * 1000).toISOString().slice(11, 19));
            } catch (error) {
              const handleMessage = event => {
                if (!event.data || event.data.type !== 'replayer:video-time' || event.data.field !== 'end') {
                  return;
                }
                window.removeEventListener('message', handleMessage);
                if (event.data.time === null) {
                  return;
                }
                setFieldValue('end', event.data.time);
                setFieldValue('endStr', new Date(event.data.time * 1000).toISOString().slice(11, 19));
              };
              window.addEventListener('message', handleMessage);
              iframeRef.contentWindow.postMessage({type: 'replayer:get-video-time', field: 'end'}, '*');
            }
          };

          const setStart = ev => {
            let iframeRef = document.getElementById("higlightIframe");
            if (!iframeRef) {
              return;
            }

            const startSeconds = hmsToSeconds(ev.target.value);
            setFieldValue('start', startSeconds);
            setFieldValue('startStr', ev.target.value);
            try {
              seekVideo(iframeRef, startSeconds);
            } catch (error) {
              // In locale il player puo' essere su una porta diversa: il seek manuale resta opzionale.
            }
          };

          const setEnd = ev => {
            let iframeRef = document.getElementById("higlightIframe");
            if (iframeRef === null) {
              return;
            }

            const endSeconds = hmsToSeconds(ev.target.value);
            setFieldValue('end', endSeconds);
            setFieldValue('endStr', ev.target.value);
            try {
              seekVideo(iframeRef, endSeconds);
            } catch (error) {
              // In locale il player puo' essere su una porta diversa: il seek manuale resta opzionale.
            }
          };

          const analysisDurationSeconds = Math.max(0, Number(values.end) - Number(values.start));
          const analysisMinutes = Math.max(1, Math.ceil(analysisDurationSeconds / 60));
          const analysisPricePerMinuteCents = analysisConfig ? Number(analysisConfig.pricePerMinuteCents) || 0 : 0;
          const analysisPriceCents = analysisMinutes * analysisPricePerMinuteCents;
          const selectedDurationSeconds = Math.max(0, Number(values.end) - Number(values.start));
          const selectedDuration = new Date(selectedDurationSeconds * 1000).toISOString().slice(11, 19);

          return (
            <Form onSubmit={handleSubmit}>
              <Modal.Body>
                <div className="skywork-highlight-intro">
                  <div className="skywork-highlight-intro-icon">{isAnalysisMode ? <FaChartBar aria-hidden="true"/> : <FaMagic aria-hidden="true"/>}</div>
                  <div>
                    <small>{isAnalysisMode ? t('HIGHLIGHT_ANALYSIS_VIDEO') : t('HIGHLIGHT_SOURCE_VIDEO')}</small>
                    <h6>{video.name}</h6>
                    <p>{isAnalysisMode ? t('HIGHLIGHT_ANALYSIS_SELECT_RANGE') : t('HIGHLIGHT_SELECT_MOMENT')}</p>
                  </div>
                </div>
                {isAnalysisMode ? (
                  <Alert variant="info">
                    {t('HIGHLIGHT_ANALYSIS_RANGE_HELP')}
                  </Alert>
                ) : null}
                {isAnalysisMode && analysisConfigLoading ? (
                  <div className="d-flex align-items-center mb-3">
                    <Spinner animation="border" size="sm" className="mr-2"/>
                    {t('HIGHLIGHT_ANALYSIS_LOADING_CONFIG')}
                  </div>
                ) : null}
                {isAnalysisMode && analysisError ? <Alert variant="danger">{analysisError}</Alert> : null}
                <section className="skywork-highlight-section skywork-highlight-player-section">
                  <div className="skywork-highlight-section-heading">
                    <FaPlay aria-hidden="true"/>
                    <div>
                      <strong>{t('HIGHLIGHT_FIND_MOMENT')}</strong>
                      <small>{t('HIGHLIGHT_FIND_MOMENT_HELP')}</small>
                    </div>
                  </div>
                  <div className="card_image_rp skywork-highlight-player">
                    <div className="videoWrapper">
                      <Row>
                        <Iframe
                          id="higlightIframe"
                          url={playerUrl}
                          allowFullScreen
                        />
                      </Row>
                    </div>
                  </div>
                </section>
                <section className="skywork-highlight-section skywork-highlight-time-section">
                  <div className="skywork-highlight-section-heading">
                    <FaClock aria-hidden="true"/>
                    <div>
                      <strong>{t('HIGHLIGHT_SELECT_INTERVAL')}</strong>
                      <small>{t('HIGHLIGHT_SELECT_INTERVAL_HELP')}</small>
                    </div>
                  </div>
                <div className="row mb-5">
                  <div className="input-group col-6">
                    <Form.Control required name="startStr" type="text" placeholder={t('START')} maxLength={8} value={values.startStr || '00:00:00'} onChange={setStart}/>
                    <div className="input-group-append">
                      <button className="btn btn-primary" type="button" onClick={setStartFromSeekTo}>{t('START')}</button>
                    </div>
                  </div>
                  <div className="input-group col-6">
                    <Form.Control required name="endStr" type="text" placeholder={t('END')} maxLength={8} value={values.endStr || '00:00:00'} onChange={setEnd}/>
                    <div className="input-group-append">
                      <button className="btn btn-primary" type="button" onClick={setEndFromSeekTo}>{t('END')}</button>
                    </div>
                  </div>
                </div>
                  <div className="skywork-highlight-duration"><FaClock aria-hidden="true"/><span>{t('HIGHLIGHT_TOTAL_DURATION')}</span><strong>{selectedDuration}</strong></div>
                </section>
                <section className="skywork-highlight-section">
                  <div className="skywork-highlight-section-heading">
                    <FaMagic aria-hidden="true"/>
                    <div><strong>{isAnalysisMode ? t('HIGHLIGHT_ANALYSIS_DETAILS') : t('HIGHLIGHT_DETAILS')}</strong><small>{t('HIGHLIGHT_DETAILS_HELP')}</small></div>
                  </div>
                  <div className="skywork-highlight-fields-grid">
                    <Form.Group>
                      <Form.Label>{t('TITLE')}*</Form.Label>
                      <Form.Control required name="name" type="text" placeholder={t('TITLE')} maxLength={255} value={values.name || ''} onChange={handleChange}/>
                    </Form.Group>
                    <Form.Group>
                      <Form.Label>{t('DESCRIPTION')}*</Form.Label>
                      <Form.Control required name="description" as="textarea" maxLength={isAnalysisMode ? 1000 : 255} placeholder={t('DESCRIPTION')} value={values.description || ''} onChange={handleChange}/>
                    </Form.Group>
                  </div>
                </section>
                {isAnalysisMode ? (
                  <div className="public-partner-match-options mb-3">
                    <div>
                      <small>{t('HIGHLIGHT_ANALYSIS_DURATION')}</small>
                      <strong>{analysisMinutes} min</strong>
                    </div>
                    <div>
                      <small>{t('COST')}</small>
                      <strong>{(analysisPriceCents / 100).toFixed(2)} EUR</strong>
                    </div>
                  </div>
                ) : null}
                <section className="skywork-highlight-section">
                  <div className="skywork-highlight-section-heading">
                    <FaVideo aria-hidden="true"/>
                    <div><strong id="highlight-camera-label">{t('CAMERAS')}</strong><small>{t('HIGHLIGHT_CAMERAS_HELP')}</small></div>
                  </div>
                    <Form.Group className="mb-0">
                      <HighlightCameraSelector
                        options={values.cameras}
                        value={values.selectedCameras}
                        onChange={value => setFieldValue('selectedCameras', value)}
                      />
                    </Form.Group>
                </section>
              </Modal.Body>
              <div className="modal-footer">
                <button type="button" className="btn skywork-highlight-cancel-btn" onClick={hideVideoHighlightModal}><FaTimes aria-hidden="true"/>{t('CANCEL')}</button>
                <Button type="submit" className="btn skywork-highlight-save-btn" disabled={isAnalysisMode && (analysisConfigLoading || analysisSubmitting || !analysisConfig)}>
                  {analysisSubmitting ? <Spinner animation="border" size="sm" className="mr-2"/> : null}
                  {analysisSubmitting ? null : <FaSave aria-hidden="true"/>}
                  {isAnalysisMode ? t('HIGHLIGHT_CONFIRM_ANALYSIS') : t('SAVE')}
                </Button>
              </div>
            </Form>
          );
        }}
      </Formik>
    </Modal>
    <Modal className="modal_rp" contentClassName="bg_blue_rp" show={!!pendingAnalysisData} onHide={closeConfirmation} centered>
      <Modal.Header closeButton>
        <Modal.Title as="h5">{t('HIGHLIGHT_CONFIRM_MATCH_ANALYSIS')}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {pendingAnalysisData ? (
          <div>
            <p>{t('HIGHLIGHT_ANALYSIS_CONFIRM_INTRO')}</p>
            <div className="public-partner-match-options mb-3">
              <div>
                <small>{t('HIGHLIGHT_ANALYSIS_DURATION')}</small>
                <strong>{pendingAnalysisData.minutes} min</strong>
              </div>
              <div>
                <small>{t('COST')}</small>
                <strong>{formatEurCents(pendingAnalysisData.priceCents)}</strong>
              </div>
              <div>
                <small>{t('AVAILABLE_CREDIT')}</small>
                <strong>{formatEurCents(walletCents)}</strong>
              </div>
            </div>
            {walletCents >= pendingAnalysisData.priceCents ? (
              <Alert variant="info">
                {t('HIGHLIGHT_ANALYSIS_PAYMENT_HELP')}
              </Alert>
            ) : (
              <Alert variant="danger">
                {t('HIGHLIGHT_ANALYSIS_INSUFFICIENT_CREDIT_HELP')}
              </Alert>
            )}
          </div>
        ) : null}
      </Modal.Body>
      <Modal.Footer>
        <Button className="btn btn_red_outline_rp" onClick={closeConfirmation} disabled={analysisSubmitting}>{t('CANCEL')}</Button>
        {pendingAnalysisData && walletCents >= pendingAnalysisData.priceCents ? (
          <Button className="btn btn_red_rp" onClick={createPendingAnalysis} disabled={analysisSubmitting}>
            {analysisSubmitting ? <Spinner animation="border" size="sm" className="mr-2"/> : null}
            {t('CONFIRM_AND_PAY')}
          </Button>
        ) : (
          <Button className="btn btn_red_rp" onClick={rechargeCredit}>
            {t('TOP_UP_CREDIT')}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
    </React.Fragment>
  );
};

PlayerHighLight.propTypes = {
  video: PropTypes.object.isRequired,
  token: PropTypes.string,
  profile: PropTypes.object,
  saveHighlight: PropTypes.func.isRequired,
  showMessageCreatingHighlight: PropTypes.func.isRequired,
  showMessageCreatingAnalysis: PropTypes.func.isRequired,
  showMessageAnalysisCreated: PropTypes.func.isRequired,
  showMessageHighlightDurationError: PropTypes.func.isRequired,
  hideVideoHighlightModal: PropTypes.func.isRequired,
  goToTopUp: PropTypes.func.isRequired,
  isVideoHighlightModalVisible: PropTypes.bool
};

PlayerHighLight.defaultProps = {
  token: null,
  profile: null,
  isVideoHighlightModalVisible: false
};

const mapStateToProps = ({login: {token, profile}, player, partner}) => {
  const videoState = profile && profile.user && profile.user.role === 1 ? partner : player;
  return {
    token,
    profile,
    permanentVideos: videoState.permanentVideos,
    selectedPermanentVideo: videoState.selectedPermanentVideo,
    isVideoHighlightModalVisible: videoState.isVideoHighlightModalVisible
  };
};

const mapDispatchToProps = dispatch => ({
  saveHighlight: (videoData) => {
    if (videoData.tracking[0].seconds >= videoData.tracking[1].seconds) {
      dispatch(showMessage('HIGHLIGHT', 'HIGHLIGHT_BAD_START_TIME'));
      return;
    }
    
    dispatch(saveHighlight(videoData));
  },
  showMessageCreatingHighlight: () => {
    dispatch(showMessage('HIGHLIGHT', 'HIGHLIGHT_OPERATION_IN_PROGRESS'));
  },
  showMessageCreatingAnalysis: () => {
    dispatch(showMessage('ANALYSIS', 'ANALYSIS_CREATION_IN_PROGRESS'));
  },
  showMessageAnalysisCreated: () => {
    dispatch(showMessage('ANALYSIS', 'VIDEO_ANALYSIS_CREATED_MESSAGE'));
  },
  showMessageHighlightDurationError: () => {
    dispatch(showMessage('HIGHLIGHT', 'HIGHLIGHT_TOO_LONG'));
  },
  hideVideoHighlightModal: () => {
    dispatch(hideVideoHighlightModal());
    dispatch(permanentVideoSelected(null));
    dispatch(partnerActions.hideVideoHighlightModal());
    dispatch(partnerActions.permanentVideoSelected(null));
  },
  goToTopUp: () => {
    dispatch(push('/user-top-up-account'));
  }
});

export default withRouter(connect(
  mapStateToProps,
  mapDispatchToProps
)(PlayerHighLight));
